/** Per workstation: one desk has the label printer beside it, the next may not [SSD-7415]. */
export const AUTO_PRINT_ON_SCAN_KEY = 'fulfillment.viewOrders.autoPrintOnScan';

/** Whether a scan prints the order's label by itself. Off unless this browser was told otherwise. */
export const loadAutoPrintOnScan = (): boolean => {
  try {
    return window.localStorage.getItem(AUTO_PRINT_ON_SCAN_KEY) === 'on';
  } catch {
    return false;
  }
};

/** An unwritable store only costs the desk the choice across a reload; the toggle still holds for now. */
export const saveAutoPrintOnScan = (on: boolean): void => {
  try {
    window.localStorage.setItem(AUTO_PRINT_ON_SCAN_KEY, on ? 'on' : 'off');
  } catch {
    // Nothing to do: the setting on screen is unaffected.
  }
};

export const AUTO_PRINTED_KEY = 'fulfillment.viewOrders.autoPrintedOrders';

/** How long this desk remembers which orders a scan already printed; an order is packed well within it. */
const AUTO_PRINTED_DAYS = 14;

const readStamps = (key: string): Record<string, number> => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '{}');
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
};

/** A stamp from a clock that ran ahead never counts, so correcting the clock cannot hold an order past the window. */
const withinWindow = (at: unknown, now: number): boolean =>
  typeof at === 'number' && Number.isFinite(at) && at <= now && at >= now - AUTO_PRINTED_DAYS * 24 * 60 * 60 * 1000;

/** Rewrites one record through `change`, dropping orders older than the window so it stays small. */
const writeStamps = (key: string, change: (kept: Record<string, number>) => void, now: number): void => {
  const kept = Object.fromEntries(Object.entries(readStamps(key)).filter(([, at]) => withinWindow(at, now)));
  change(kept);
  try {
    window.localStorage.setItem(key, JSON.stringify(kept));
  } catch {
    // An unwritable store only means a later scan may print again; the label in hand is the same.
  }
};

/** Whether a scan on this PC printed this order's label within the window, so a later scan does not print a second one. */
export const autoPrintedBefore = (orderId: number, now: number = Date.now()): boolean =>
  withinWindow(readStamps(AUTO_PRINTED_KEY)[String(orderId)], now);

/** Remembers the scan print. */
export const rememberAutoPrint = (orderId: number, now: number = Date.now()): void =>
  writeStamps(
    AUTO_PRINTED_KEY,
    (kept) => {
      kept[String(orderId)] = now;
    },
    now,
  );

/** A scan print that failed is not a label in hand, so the next scan may try again. */
export const forgetAutoPrint = (orderId: number, now: number = Date.now()): void =>
  writeStamps(
    AUTO_PRINTED_KEY,
    (kept) => {
      delete kept[String(orderId)];
    },
    now,
  );

export const AUTO_LABEL_OWED_KEY = 'fulfillment.viewOrders.autoPrintLabelOwed';

/**
 * A scan print that went out before the carrier issued a label printed the PACKING alone. The order stays printed
 * for a scan that opens it again, but the last bought product scanned in still owes the desk its carrier label.
 */
export const labelOwed = (orderId: number, now: number = Date.now()): boolean =>
  withinWindow(readStamps(AUTO_LABEL_OWED_KEY)[String(orderId)], now);

export const rememberLabelOwed = (orderId: number, now: number = Date.now()): void =>
  writeStamps(
    AUTO_LABEL_OWED_KEY,
    (kept) => {
      kept[String(orderId)] = now;
    },
    now,
  );

export const forgetLabelOwed = (orderId: number, now: number = Date.now()): void =>
  writeStamps(
    AUTO_LABEL_OWED_KEY,
    (kept) => {
      delete kept[String(orderId)];
    },
    now,
  );
